import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { useRouteMatch } from 'react-router';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ButtonNew from './inputs/ButtonNew';
import { addAbundancesToNodes } from 'constants/fcm';
import { resetMatrix } from 'actions/matrices';
import { setNodeOverrides as setPlayModeNodeOverrides } from 'actions/matrixSettings';
import './NodesView.scss';
import classNames from 'classnames';
import PlayModeWhatChanged from './PlayModeWhatChanged';
import TrophicLevels from 'components/TrophicLevels';
import { usePlayModeState } from '../state/PlayModeState';
import DetailsPage from './DetailsPage';

const trophicCascadeLevels = [
  'Primary',
  'Secondary',
  'Tertiary',
  'Equilibrium',
];

const NodesViewHeaderText = ({ canRunModel, modelHasRun }) => {
  if (canRunModel) {
    return (
      <div className="intro-paragraph">
        Run the <span className="intro-paragraph-key-word">model</span> to see
        the <span className="intro-paragraph-key-word">results</span> of
        changing the <span className="intro-paragraph-key-word">abundance</span>{' '}
        of an <span className="intro-paragraph-key-word">input</span>.
      </div>
    );
  } else if (modelHasRun) {
    return (
      <div className="intro-paragraph">
        The relative impacts to the matrix are shown below. The{' '}
        <span className="intro-paragraph-key-word">results</span> panel allows
        you to <span className="intro-paragraph-key-word">compare</span> those
        changes to the original state.{' '}
        <span className="intro-paragraph-key-word">Reset</span> returns the
        matrix to its equilibrium state.
      </div>
    );
  } else {
    return (
      <div className="intro-paragraph">
        The members of an ecosystem are represented by{' '}
        <span className="intro-paragraph-key-word">nodes</span>, which are
        arranged{' '}
        <span className="intro-paragraph-key-word">by trophic level</span>. The
        way in which nodes impact each other is show in the{' '}
        <span className="intro-paragraph-key-word">by connections</span> view. A
        node’s relative representation in an ecosystem is described by its{' '}
        <span className="intro-paragraph-key-word">abundance</span>. Adjust an{' '}
        <span className="intro-paragraph-key-word">input</span> to the matrix to
        see the impacts cascade.
      </div>
    );
  }
};

const NodesView = ({ matrix, base, viewMode, chartView, setChartView }) => {
  const dispatch = useDispatch();
  const resetMatrixState = useSelector(state => state.matrices.resetToInitial);
  let { url } = useRouteMatch();
  let mounted = useRef(false);

  const nodeResultsState = usePlayModeState(state => state.nodeResults);
  const setNodeResultsState = usePlayModeState(state => state.setNodeResults);
  const resetNodesAnimatedState = usePlayModeState(
    state => state.resetNodesAnimated
  );

  const nodeOverridesState = useSelector(
    state => state.matrixSettings.playModeNodeOverrides
  );

  const [currentViewMode, setCurrentViewMode] = useState(viewMode);

  // Uses a state to keep track of the current abundance
  // override settings
  const [nodeOverrides, setNodeOverrides] = useState([]);

  useEffect(() => {
    dispatch(setPlayModeNodeOverrides(nodeOverrides));
  }, [dispatch, nodeOverrides]);

  // Uses a state to keep track of the current trophicCascadeLevel
  const [trophicCascadeLevel, setTrophicCascadeLevel] = useState(
    trophicCascadeLevels[trophicCascadeLevels.length - 1]
  );

  // the cascadeRunning interval
  const [cascadeRunning, setCascadeRunning] = useState([]);

  // Starts a cascade animation
  const startCascade = useCallback(
    noAnimate => {
      // Clear any running cascade animations
      if (cascadeRunning) clearInterval(cascadeRunning);

      if (!noAnimate) {
        // Starts at the first level
        let trophicCascadeLevel = trophicCascadeLevels[0];
        setTrophicCascadeLevel(trophicCascadeLevel);

        // override it locally because of setInterval idiosyncrasies
        const cascadeRunningLocal = setInterval(() => {
          trophicCascadeLevel =
            trophicCascadeLevels[
              trophicCascadeLevels.indexOf(trophicCascadeLevel) + 1
            ];
          if (trophicCascadeLevel) setTrophicCascadeLevel(trophicCascadeLevel);
          if (
            !trophicCascadeLevel ||
            trophicCascadeLevels.indexOf(trophicCascadeLevel) ===
              trophicCascadeLevels.length - 1
          ) {
            clearInterval(cascadeRunningLocal);
            setCascadeRunning(null);
          }
        }, 1000);

        setCascadeRunning(cascadeRunningLocal);
      }
    },
    [cascadeRunning]
  );

  useEffect(() => {
    return () => {
      if (cascadeRunning) clearInterval(cascadeRunning);
    };
  }, [cascadeRunning]);

  // Keep track of the previous node abundances
  const [prevNodes, setPrevNodes] = useState([]);

  // Adds / updates the abundance values
  const currentNodes = addAbundancesToNodes(matrix, nodeOverrides, prevNodes);

  // when resetMatrix is passed, clear all nodeOverrides
  useEffect(() => {
    if (resetMatrixState) {
      setPrevNodes([]);
      setNodeOverrides([]);
      setNodesToOverride([]);
      resetNodesAnimatedState();
      setNodeResultsState(null);
      // Reset the reset flag
      dispatch(resetMatrix(false));
    }
  }, [resetMatrixState, dispatch]);

  const handleReset = useCallback(() => {
    // Clear any running cascade animations
    if (cascadeRunning) clearInterval(cascadeRunning);
    setCascadeRunning(null);
    setTrophicCascadeLevel(
      trophicCascadeLevels[trophicCascadeLevels.length - 1]
    );
    dispatch(resetMatrix(true));
  }, [cascadeRunning, dispatch]);

  // When using the "run model" button, make all changes not live
  const [nodesToOverride, setNodesToOverride] = useState(
    nodeOverridesState ?? []
  );

  const modelHasRun = useMemo(() => {
    const filterOldAbundance = nodesToFilter => {
      return nodesToFilter.map(n => {
        let clone = JSON.parse(JSON.stringify(n));
        delete clone?.oldAbundance;
        return clone;
      });
    };
    let hasRun = _.isEqual(
      filterOldAbundance(nodesToOverride),
      filterOldAbundance(nodeOverrides)
    );
    if (!hasRun) return false;
    hasRun = nodeOverrides.some(n => {
      const currentNode = currentNodes.find(node => node.id === n.id);
      return (
        n?.abundanceResult !==
        (currentNode?.abundanceResult ?? currentNode?.abundance)
      );
    });
    return hasRun;
  }, [currentNodes, nodeOverrides, nodesToOverride]);

  // This is gross, but basically currentNodes needs a second to reset because of how
  // state gets propagated on a remount
  useEffect(() => {
    const noUpdate =
      currentNodes.every(
        n => n.abundanceResult === n.originalAbundanceResult
      ) &&
      nodeResultsState?.some(
        n => n.abundanceResult !== n.originalAbundanceResult
      );

    if (
      !!nodeResultsState &&
      !noUpdate &&
      !_.isEqual(currentNodes, nodeResultsState)
    ) {
      setNodeResultsState(currentNodes);
    }
  }, [setNodeResultsState, currentNodes, nodeResultsState]);

  const currentNodesRef = useRef(currentNodes);

  useEffect(() => {
    if (currentNodes) {
      currentNodesRef.current = currentNodes;
    }
  }, [currentNodes]);

  // This is our normal setting behavior
  useEffect(() => {
    if (nodeOverrides && nodeOverrides.length) {
      setNodeResultsState(currentNodesRef.current);
    }
  }, [setNodeResultsState, nodeOverrides]);

  // Explicit version of above
  const addNodeOverridesExplicit = useCallback(
    noAnimate => {
      setPrevNodes(currentNodes);

      let nextNodes = [...nodeOverrides];

      for (const node of nodesToOverride) {
        // Adds node to nodesOverride if it doesn't already exist
        const prevNode = prevNodes.find(n => n.id === node.id) || {};

        nextNodes = [
          ...nextNodes.filter(n => n.id !== node.id),
          {
            ...node,
            // Get the old abundance value from prevNodes
            oldAbundance:
              (prevNode.fixed
                ? prevNode.abundance
                : prevNode.abundanceResult) || node.oldAbundance,
            fixed: true,
          },
        ];
      }

      setNodeOverrides(nextNodes);

      startCascade(noAnimate);
    },
    [prevNodes, currentNodes, nodesToOverride, nodeOverrides, startCascade]
  );

  useEffect(() => {
    // Make sure we reset things if editing
    if (viewMode !== currentViewMode) {
      if (viewMode === 'edit' && currentViewMode === 'play') {
        handleReset();
      }
      setCurrentViewMode(viewMode);
    }
  }, [currentViewMode, handleReset, viewMode, url, matrix]);

  useEffect(() => {
    // Make sure we reset things if editing or going back to incompatible page
    const pagePathEnd = url.split('/').pop();
    const isIncompatiblePage = pagePathEnd !== matrix?.id;

    if (isIncompatiblePage && chartView) {
    }
  }, [chartView, url, matrix]);

  // on mount, use node overrides if present
  useEffect(() => {
    if (!mounted.current) {
      if (nodeOverridesState?.length) {
        addNodeOverridesExplicit(true);
      }
    }
  }, []);

  // on mount
  useEffect(() => {
    mounted.current = true;
  }, []);

  const allAbundancesKey = useMemo(() => {
    return Object.values(matrix.nodes)
      .map(n => `${n.id}_${n.abundance}`)
      .sort()
      .join('-');
  }, [matrix]);

  return (
    <div className="NodesView">
      <div className="NodesView-content">
        <div className="NodesView-header">
          <NodesViewHeaderText
            canRunModel={!_.isEqual(nodesToOverride, nodeOverrides)}
            modelHasRun={modelHasRun}
          />

          <div className="NodesView-header-button-container">
            <ButtonNew
              buttonClasses={classNames('secondary-button-small', {
                disabled: !modelHasRun,
              })}
              onClick={handleReset}
              label="Reset"
              iconLeft="reseticon"
            />

            {_.isEqual(nodesToOverride, nodeOverrides) ? (
              <div
                data-tooltip-id="run-model-tooltip"
                data-tooltip-content="Change the abundance of a node with the slider to run the model"
                data-tooltip-place="bottom"
              >
                <ButtonNew
                  buttonClasses={classNames(
                    'primary-button-small tour-run-model-button',
                    {
                      disabled: _.isEqual(nodesToOverride, nodeOverrides),
                    }
                  )}
                  onClick={addNodeOverridesExplicit}
                  label="Run model"
                  iconLeft="play"
                />
              </div>
            ) : (
              <ButtonNew
                buttonClasses={classNames(
                  'primary-button-small tour-run-model-button',
                  {
                    disabled: _.isEqual(nodesToOverride, nodeOverrides),
                  }
                )}
                onClick={addNodeOverridesExplicit}
                label="Run model"
                iconLeft="play"
              />
            )}
          </div>
        </div>

        <TrophicLevels
          key={allAbundancesKey}
          matrix={matrix}
          viewMode={viewMode}
          currentNodes={currentNodes}
          nodeOverrides={nodeOverrides}
          nodesToOverride={nodesToOverride}
          setNodesToOverride={setNodesToOverride}
          trophicCascadeLevel={trophicCascadeLevel}
        />
      </div>
      {chartView ? (
        <div className="NodesView-content-cover">
          <DetailsPage
            base={base}
            matrix={matrix}
            goBack={() => setChartView(false)}
          />
        </div>
      ) : null}

      <ReactTooltip id="run-model-tooltip" className="react-tooltip" />
    </div>
  );
};

export default NodesView;
